import React, { useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Radio, Stack, Textarea } from '@mantine/core'

import { EventResultOptions } from '@/components/Event/NewScript/components/common/EventResultOptions'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { SubtleButton } from '@/components/Event/NewScript/components/common/Layout/SubtleButton'
import {
  ActionContainer,
  ActionTitle,
} from '@/components/Event/NewScript/components/common/ScriptTimeline'
import { canceledEventSchema } from '@/components/Event/NewScript/schemas/canceledEventSchema'
import { getBaseStepKey } from '@/components/Event/NewScript/utils/stepKey'
import {
  DepositionResult,
  REASON_FOR_CNA_LABEL,
  ReasonForCna,
  ScriptStep,
} from '@/constants'
import { Event, EventScriptDataObject } from '@/types'
import { getCurrentTimeInUtc } from '@/utils/time'

export type EventResultData = Pick<
  EventScriptDataObject,
  | 'result'
  | 'notes'
  | 'reason_for_cna'
  | 'other_reason'
  | 'cna_declared_at'
  | 'last_timestamp'
>

type Props = {
  title?: string
  onSubmitClick: (data: EventResultData) => void
  event: Event
  value: EventResultData
  canceledFromStep: ScriptStep | string | null
}

export const EventCancelledCardEdit: React.FC<Props> = ({
  title,
  onSubmitClick,
  event,
  value,
  canceledFromStep,
}) => {
  const methods = useForm({
    resolver: yupResolver(canceledEventSchema),
    mode: 'all',
    defaultValues: {
      ...value,
      reason_for_cna: value.reason_for_cna ?? undefined,
      no_show_is_off_record: true,
      no_show_is_on_record: true,
    },
  })

  const { control, register } = methods

  const result = useWatch({ control, name: 'result' })
  const cnaDeclaredAt = useWatch({ control, name: 'cna_declared_at' })
  const isCNA = result === DepositionResult.CNA
  const canceledFromBaseStep = getBaseStepKey(canceledFromStep)

  useEffect(() => {
    // if the event was cancelled/rescheduled and reviewer decide to change event result to CNA in review step then
    // we need to set the missing `cna_declared_at` value
    if (isCNA && !cnaDeclaredAt) {
      const currentTime = getCurrentTimeInUtc()
      methods.setValue('last_timestamp', currentTime)
      methods.setValue('cna_declared_at', currentTime)
    }
  }, [isCNA, cnaDeclaredAt, methods])

  const onSubmitClickHandler = async () => {
    const isValid = await methods.trigger()
    if (isValid) {
      onSubmitClick(methods.getValues())
    }
  }

  return (
    <ActionContainer outer>
      {title && <ActionTitle mb="md">{title}</ActionTitle>}
      <ActionContainer>
        <Stack align="flex-start">
          <Stack spacing={40}>
            <EventResultOptions
              event={event}
              control={control}
              canceledFromStep={canceledFromBaseStep}
            />
            {canceledFromBaseStep !== ScriptStep.ATTENDANCE && (
              <Textarea
                {...register('notes')}
                label="Notes"
                placeholder="How was the event? (Anything worth mentioning?)"
                minRows={3}
              />
            )}
            {isCNA && (
              <>
                <Controller
                  name="reason_for_cna"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      {...field}
                      onChange={value => {
                        field.onChange(value as ReasonForCna)
                        methods.trigger('other_reason')
                      }}
                    >
                      <Stack>
                        {Object.values(ReasonForCna).map(value => (
                          <Radio
                            styles={{ radio: { cursor: 'pointer' } }}
                            key={value}
                            value={value}
                            label={REASON_FOR_CNA_LABEL[value]}
                          />
                        ))}
                      </Stack>
                    </Radio.Group>
                  )}
                />
                {methods.formState.errors.reason_for_cna !== undefined && (
                  <FormErrors
                    errors={methods.formState.errors.reason_for_cna.message}
                  />
                )}
                <Textarea
                  {...register('other_reason')}
                  label="Specify the other reason (will be visible on the certificate)"
                  placeholder="How was the event? (Anything worth mentioning?)"
                  minRows={3}
                />
                {methods.formState.errors.other_reason !== undefined && (
                  <FormErrors
                    errors={methods.formState.errors.other_reason.message}
                  />
                )}
              </>
            )}
          </Stack>
          <SubtleButton p={0} onClick={onSubmitClickHandler}>
            Save changes
          </SubtleButton>
        </Stack>
      </ActionContainer>
    </ActionContainer>
  )
}
